import type { ApiResponse } from '@/shared/types';
import api from '@/shared/api/client';

export const operatorTenantApi = {
  listTenants: (params?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get<ApiResponse<any>>('/operator/tenants', { params }),

  createTenant: (data: {
    name: string;
    slug: string;
    status?: string;
    adminEmail: string;
    adminPassword: string;
    adminName?: string;
    quotaPlanId?: string;
  }) => api.post<ApiResponse<any>>('/operator/tenants', data),

  updateTenant: (id: string, data: any) =>
    api.put<ApiResponse<any>>(`/operator/tenants/${id}`, data),

  presignTenantLogo: (
    tenantId: string,
    data: { fileName: string; mimeType: string; fileSize: number },
  ) =>
    api.post<ApiResponse<{ uploadUrl: string; storageKey: string; publicUrl: string }>>(
      `/operator/tenants/${tenantId}/logo/presign`,
      data,
    ),

  assignQuotaPlan: (tenantId: string, quotaPlanId: string | null) =>
    api.put<ApiResponse<any>>(`/operator/tenants/${tenantId}/quota-plan`, {
      quotaPlanId,
    }),

  listTenantUsers: (tenantId: string) =>
    api.get<ApiResponse<any[]>>(`/operator/tenants/${tenantId}/users`),

  listTenantMemberRoles: (tenantId: string) =>
    api.get<ApiResponse<any[]>>(`/operator/tenants/${tenantId}/member-roles`),

  createTenantUser: (tenantId: string, data: any) =>
    api.post<ApiResponse<any>>(`/operator/tenants/${tenantId}/users`, data),

  updateTenantUser: (userId: string, data: any) =>
    api.put<ApiResponse<any>>(`/operator/users/${userId}`, data),

  getFeatures: () => api.get<ApiResponse<any>>('/operator/features'),

  updateTenantFeatures: (
    tenantId: string,
    data: {
      enabledMenus?: string[];
      enabledFeatures?: Record<string, boolean>;
    },
  ) =>
    api.put<ApiResponse<any>>(`/operator/tenants/${tenantId}/features`, data),

  listTrialApplications: (params?: Record<string, any>) =>
    api.get<ApiResponse<any>>('/operator/trial-applications', { params }),

  getTrialApplication: (id: string) =>
    api.get<ApiResponse<any>>(`/operator/trial-applications/${id}`),

  approveTrialApplication: (id: string, data: any) =>
    api.post<ApiResponse<any>>(
      `/operator/trial-applications/${id}/approve`,
      data,
    ),

  rejectTrialApplication: (id: string, reason: string) =>
    api.post<ApiResponse<any>>(`/operator/trial-applications/${id}/reject`, {
      reason,
    }),

  impersonateTenant: (tenantId: string) =>
    api.post<ApiResponse<any>>(`/operator/tenants/${tenantId}/impersonate`),

  getTenantHealth: (tenantId: string) =>
    api.get<ApiResponse<TenantHealthSnapshot>>(`/operator/tenants/${tenantId}/health`),
};

export type TenantHealthSnapshot = {
  tenantId: string;
  status: 'healthy' | 'warning' | 'critical';
  subscription: {
    status: string | null;
    planName: string | null;
    periodEnd: string | null;
  };
  usage: {
    units: { used: number; limit: number | null; percent: number | null };
    apiCalls: { used: number; limit: number | null; percent: number | null };
    storageMb: { used: number; limit: number | null; percent: number | null };
  };
  dunning: {
    status: string | null;
    daysPastDue: number;
  };
  feedback: {
    unreadCount: number;
  };
  outbox: {
    deadCount: number;
  };
  alerts: Array<{
    code: string;
    severity: 'warning' | 'critical';
    message: string;
    detail?: Record<string, unknown>;
  }>;
  evaluatedAt: string;
};
